import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

const performanceReviewsFirstPeriod = [
  {
    title: 'Biggest Accomplishments',
    badge: '⭐ Star Contributor',
    company: 'Valtech PT',
    content: `Led the Contentful integration for Rijkzwaan, enabling a global rollout in over 30 countries. Post-launch, became the sole front-end developer and successfully owned the delivery. Presented the case study at a global Valtech event to over 100 peers. Also played a key role in the 100th anniversary redesign, helping the client go live under tight deadlines. Achieved the Contentful Professional Certification and contributed to performance improvements and internal tooling.`,
    quotes: [
      `“You became the only FE at RZ and you demonstrated that you were up to the challenge.” – Manager`,
    ],
  },
  {
    title: 'Biggest Lessons',
    badge: '📘 Learner',
    company: 'Valtech PT',
    content: `Developed strategies to manage client expectations and navigate disorganized planning cycles. Learned to work constructively with different team dynamics and improved my ability to bring calm and structure to challenging situations.`,
    quotes: [
      `“You managed to share your thoughts thinking about the benefits for the team and client.” – Manager`,
    ],
  },
  {
    title: 'Contribution to Culture',
    badge: '🌈 Culture Builder',
    company: 'Valtech Portugal',
    content: `Actively contributed to the local culture by working from the office regularly and attending nearly every event, from internal talks to informal gatherings. Participated in the Tech Girl initiative to teach programming basics to girls, and helped launch Frontend Days in Portugal, organizing and presenting multiple sessions.`,
    quotes: [
      `“Even when she is having a bad day, she always smiles when she enters the office.” – Peer`,
      `“She represents the core values of Valtech Portugal.” – Peer Feedback`,
    ],
  },
  {
    title: 'Initiative & Accountability',
    badge: '🎯 Ownership',
    company: 'Valtech PT',
    content: `Took ownership of tech debt, leading performance improvements and integrating SonarQube. Transparently handled a release mistake and resolved it quickly while keeping client trust. Led sprint planning efforts and supported backlog grooming and story refinement.`,
    quotes: [
      `“She led the performance improvements epic at RZ with great results.” – Peer`,
      `“That sense of ownership is rare.” – Peer Feedback`,
    ],
  },
  {
    title: 'Manager Feedback',
    badge: '💬 Feedback',
    company: 'Valtech PT',
    content: `Delivered complex features independently while adapting to project shifts. Managed client pressure effectively and supported team onboarding. Presented at global events and earned a Contentful certification. Recommended to grow influence in composable solutions and stakeholder collaboration.`,
    quotes: [
      `“You're an inspiration.” – Manager`,
      `“This last year was full of challenges – but they were overcome. Congratulations!” – Manager`,
      `“You consistently delivered and adapted under pressure.” – Manager`,
    ],
  },
  {
    title: 'Peer Feedback',
    badge: '🌟 Teammate',
    company: 'Valtech PT',
    content: `Recognized for positive energy, clear communication, and strong leadership within the team. Played key roles in both the Contentful integration and anniversary redesign. Deeply involved in community efforts and encouraged to continue exploring infrastructure and architecture topics.`,
    quotes: [
      `“She is one of our shining stars.” – Peer`,
      `“Without her it would be hard to keep doing the FE Days.” – Peer`,
      `“Even when she is having a bad day, she always smiles when she enters the office.” – Peer`,
    ],
  },
];

const performanceReviewsSecondPeriod = [
  {
    title: 'Promotion & Impact',
    badge: '🚀 Growth',
    company: 'Valtech PT',
    content: `After my promotion, I quickly adapted to new responsibilities and made immediate impact. Delivered a feature that empowers users to preview configurations before go-live. Contributed to knowledge transfer in my previous project and mentored young talent.`,
    quotes: [
      `“Rita you did an amazing job in your last 6 months. Being promoted increased your responsibilities, but you delivered.” – Manager`,
    ],
  },
  {
    title: 'Community Contributions',
    badge: '💡 Mentor',
    company: 'Valtech PT',
    content: `Continued organizing Frontend Day sessions, participated in mentoring young developers, and hosted the Tech Girl workshop introducing HTML, CSS, and AI to young women.`,
    quotes: [
      `“Rita is our hands-on fairy. Always willing to help with love.” – Manager`,
    ],
  },
  {
    title: 'Peer Recognition',
    badge: '🤝 Supportive Teammate',
    company: 'Valtech PT',
    content: `Peers recognize my reliability, communication, and leadership. Took initiative even without full access or clear specs and always delivered with quality.`,
    quotes: [
      `“She is someone you can rely on for anything.” – Peer`,
      `“Rita has a calm confidence that inspires others.” – Peer`,
      `“She always meets team expectations and brings positive energy.” – Peer`,
    ],
  },
];

const performanceReviewsLatestPeriod = [
  {
    title: 'Peer Review — Team Culture & Collaboration',
    badge: '🤝 Peer feedback',
    company: 'Valtech PT',
    content: `In this feedback, I was recognized for my impact on team culture, especially in how I read the team’s mood, create space for healthy communication, and help keep energy positive in remote setups. I was also praised for my critical thinking, solution-oriented mindset, and my ability to collaborate while listening carefully to others.`,
    quotes: [
      `“She contributes in a kind and direct way and helps keep spirits high.” – Peer`,
      `“Her critical thinking and quick understanding of complex topics supports the team tremendously.” – Peer`,
    ],
  },
  {
    title: 'Peer Review — Strengths & Improvement Area',
    badge: '🌱 Growth feedback',
    company: 'Valtech PT',
    content: `In this feedback, I was described as a natural team player who is always ready to help, open-minded in discussions, and confident when sharing technical opinions. A recurring positive note was my reliability, thoughtful feedback, and collaborative approach across the team.`,
    quotes: [
      `“Rita is a natural team player and works smoothly with all members of the team.” – Peer`,
      `“I can rely on her effort and valuable feedback.” – Peer`,
    ],
  },
  {
    title: 'Manager Review — Summary',
    badge: '💬 Manager feedback',
    company: 'Valtech PT',
    content: `Manager feedback highlights solid execution, visible contribution to team culture, and trust in my growth path. At the same time, it gives me a clear direction: keep improving communication impact, keep strengthening leadership skills, and get more opportunities to grow with React.`,
    quotes: [
      `“You are clearly operating at senior level and have potential to become a great leader.” – Manager`,
      `“You are essential to the team and your contribution to culture is visible.” – Manager`,
    ],
  },
];

const PerformanceReviewPage = () => {
  const [openSectionsFirst, setOpenSectionsFirst] = useState(new Set());
  const [openSectionsSecond, setOpenSectionsSecond] = useState(new Set());
  const [openSectionsLatest, setOpenSectionsLatest] = useState(new Set());

  const toggleSection = (index, period = 'first') => {
    const map = {
      first: [openSectionsFirst, setOpenSectionsFirst],
      second: [openSectionsSecond, setOpenSectionsSecond],
      latest: [openSectionsLatest, setOpenSectionsLatest],
    };

    const [current, setter] = map[period];
    const nextSet = new Set(current);
    nextSet.has(index) ? nextSet.delete(index) : nextSet.add(index);
    setter(nextSet);
  };

  const renderSection = (section, index, period = 'first') => {
    let isOpen = openSectionsFirst.has(index);
    if (period === 'second') {
      isOpen = openSectionsSecond.has(index);
    } else if (period === 'latest') {
      isOpen = openSectionsLatest.has(index);
    }
    const sectionId = `section-${period}-${index}`;
    return (
      <div
        key={index}
        className="bg-white rounded-xl shadow-md p-4 transition-all"
      >
        <button
          onClick={() => toggleSection(index, period)}
          className="w-full flex justify-between items-center text-left text-teal-800 font-semibold text-lg"
          aria-expanded={isOpen}
          aria-controls={sectionId}
        >
          <span>
            {section.title}
            <span className="ml-2 text-sm bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full">
              {section.badge}
            </span>
          </span>
          {isOpen ? (
            <ChevronUpIcon className="w-5 h-5 text-teal-600" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-teal-600" />
          )}
        </button>

        {isOpen && (
          <section
            id={sectionId}
            aria-labelledby={`btn-${sectionId}`}
            className="mt-4 text-gray-700 text-sm space-y-2 animate-fadeIn"
          >
            <p className="whitespace-pre-line">{section.content}</p>
            <p className="text-xs text-gray-400 italic">
              Company: {section.company}
            </p>
            <ul className="text-gray-500 italic space-y-1 list-disc list-inside">
              {section.quotes.map((quote) => (
                <li key={`${section.title}-${quote}`}>{quote}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-orange-100 py-16 px-6 font-sans">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-teal-700 mb-6 flex items-center gap-3">
          <span
            className="inline-block w-4 h-4 bg-teal-600 rounded-full animate-ping"
            aria-hidden="true"
          />
          2023–2024 Performance Review
        </h1>

        <div className="space-y-4 mb-10">
          {performanceReviewsFirstPeriod.map((section, index) =>
            renderSection(section, index)
          )}
        </div>

        <h2 className="text-3xl font-bold text-teal-700 mb-6 mt-12 flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 bg-teal-500 rounded-full animate-ping"
            aria-hidden="true"
          />
          H2 2024 Review (Jul–Dec)
        </h2>

        <div className="space-y-4">
          {performanceReviewsSecondPeriod.map((section, index) =>
            renderSection(section, index, 'second')
          )}
        </div>

        <h2 className="text-3xl font-bold text-teal-700 mb-6 mt-12 flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 bg-teal-500 rounded-full animate-ping"
            aria-hidden="true"
          />
          Latest Review — Last 6 Months of 2025
        </h2>

        <div className="space-y-4">
          {performanceReviewsLatestPeriod.map((section, index) =>
            renderSection(section, index, 'latest')
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceReviewPage;
